import React, { useEffect, useState, useRef, useMemo } from "react";
import { Box, useMediaQuery, Button as MuiButton } from "@mui/material";
import { useSelector } from "react-redux";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { Formik } from "formik";
import { pickBy } from "lodash";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import * as Sentry from "@sentry/react";

import Button from "../../../components/common/Button";
import Dropdown from "../../../components/common/new/Dropdown/Dropdown";
import StructureFilter from "../components/StructureFilter/StructureFilter";
import {
  selectOSKeysByUserRole,
  selectUserStructureIds,
} from "../../../redux/selectors/user";
import AttendanceAPI from "../../../services/api/attendance";
import styles from "./Attendance.styles";
import Style from "./NewAttendance.styles";

const MEETING_LABEL = "meetingType";
const DIVISION_ID = "divisionId";

const MeetingTypes = [
  { key: "Discussion", value: "DISCUSSION_MEETING" },
  { key: "Gosho", value: "GOSHO_MEETING" },
];

const DIVISION_OPTIONS = [
  { value: 1, key: "MD" },
  { value: 2, key: "WD" },
  { value: 37, key: "YMD" },
  { value: 3, key: "YWD" },
];

function Attendance() {
  const [, setAttendanceReport] = useState(null);
  const disabledOSKeys = useSelector(selectOSKeysByUserRole);
  const userStructureIds = useSelector(selectUserStructureIds);
  const [loading, setLoading] = useState(true);
  const formRef = useRef(null);
  const [showFilters, setShowFilters] = useState(false);
  const [hover, setHover] = useState(false);

  const [chartData, setChartData] = useState({
    categories: [],
    total: [],
    present: [],
    regionSeries: [],
  });

  const onSearch = async (values) => {
    try {
      setLoading(true);
      const payload = pickBy(values, Boolean);
      const response = await AttendanceAPI.getAttendanceReport(payload);

      // console.log("🔥 Full API Response:", response);
      const apiData = response?.data || [];
      setAttendanceReport(apiData);

      // --- Extract Categories ---
      const categories = apiData.map((month) => month.name);

      // --- Extract Totals ---
      const totalMembersData = apiData.map(
        (month) =>
          month.data?.find((d) => d.id === "total")?.extraData
            ?.totalMemberCount || 0
      );
      const presentMembersData = apiData.map(
        (month) =>
          month.data?.find((d) => d.id === "total")?.extraData
            ?.totalMemberAttendees || 0
      );

      // --- Extract Per-Region Data (Delhi - West, South India, etc.) ---
      const regionNames =
        apiData[0]?.data?.filter((r) => r.id !== "total")?.map((r) => r.name) ||
        [];

      const regionSeries = regionNames.map((regionName) => ({
        name: regionName,
        data: apiData.map(
          (month) =>
            month.data?.find((d) => d.name === regionName)?.extraData
              ?.totalMemberAttendees || 0
        ),
      }));

      // console.log("✅ Chart Data:", {
      //   categories,
      //   totalMembersData,
      //   presentMembersData,
      //   regionSeries,
      // });

      setChartData({
        categories,
        total: totalMembersData,
        present: presentMembersData,
        regionSeries,
      });
    } catch (error) {
      console.error("❌ Error fetching attendance:", error);
      Sentry.captureException(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    onSearch({ [MEETING_LABEL]: "DISCUSSION_MEETING", ...userStructureIds });
    // eslint-disable-next-line
  }, [userStructureIds]);

  const handleClearType = () => {
    formRef.current.setFieldValue(DIVISION_ID, "");
  };

  const isMobile = useMediaQuery("(max-width: 768px)");
  const getContainerStyle = () => ({
    flex: "1 1 220px",
    maxWidth: "300px",
    marginBottom: "8px",
  });

  const cardStyle = {
    borderRadius: "28px",
    background: "linear-gradient(135deg, #fff 82%, #f6f8ff 100%)",
    boxShadow: hover
      ? "0 10px 48px 0 rgba(37, 41, 73, .14), 0 4px 12px 0 rgba(37, 41, 73, .06)"
      : "0 6px 32px 0 rgba(37,41,73,.10), 0 1.5px 4px 0 rgba(37,41,73,.02)",
    padding: "28px 26px 20px 26px",
    transition:
      "box-shadow 0.22s cubic-bezier(.4,0,.2,1), transform 0.19s cubic-bezier(.4,0,.2,1)",
    minHeight: "430px",
    willChange: "transform",
    transform: hover ? "translateY(-5px) scale(1.016)" : "none",
  };

  const lineChartOptions = useMemo(() => {
    const baseSeries = [
      { name: "Total Members", data: chartData.total, color: "#F8CB8D" },
      { name: "Present Members", data: chartData.present, color: "#82C99E" },
    ];

    // Add dynamic region series
    const allSeries = [
      ...baseSeries,
      // ...chartData.regionSeries.map((r) => ({
      //   ...r,
      //   color: undefined, // let Highcharts auto-pick color for each region
      //   dashStyle: "ShortDash",
      //   marker: { enabled: false },
      // })),
    ];

    return {
      chart: { type: "spline", height: 340, backgroundColor: "transparent" },
      title: { text: "" },
      xAxis: {
        categories: chartData.categories,
        title: { text: "Month" },
        labels: { style: { fontSize: "14px" } },
        lineColor: "#e5e5ef",
        tickColor: "#e5e5ef",
      },
      yAxis: {
        min: 0,
        title: { text: "Members", style: { fontSize: "15px" } },
        gridLineColor: "#e5e5ef",
        labels: { style: { fontSize: "14px" } },
      },
      tooltip: {
        shared: true,
        useHTML: true,
        headerFormat: "<small>{point.key}</small><table>",
        pointFormat:
          "<tr><td style='color: {series.color}'>{series.name}: </td>" +
          "<td style='text-align: right'><b>{point.y}</b></td></tr>",
        footerFormat: "</table>",
        valueDecimals: 0,
      },
      legend: {
        layout: "horizontal",
        align: "right",
        verticalAlign: "top",
        itemStyle: { fontSize: "14px" },
      },
      plotOptions: {
        spline: {
          marker: { enabled: true, symbol: "circle", radius: 5 },
          lineWidth: 2.5,
        },
      },
      series: allSeries,
      credits: { enabled: false },
    };
  }, [chartData]);

  return (
    <Box
      style={cardStyle}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="dashboard-card"
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <span
            style={{
              fontFamily: "Inter,Roboto,sans-serif",
              fontWeight: 700,
              fontSize: 18,
              letterSpacing: "-.01em",
              color: "#283359",
              display: "flex",
              alignItems: "center",
              textShadow: "0 2px 10px rgba(93,148,255,0.07)",
              flexDirection: "row",
              gap: "6px",
            }}
          >
            Meeting Attendance
            <p style={{ fontWeight: 400, fontSize: 14, margin: 0 }}>
              (Last 3 months report)
            </p>
          </span>
        </Box>
        <MuiButton
          variant="outlined"
          size="small"
          startIcon={showFilters ? <RemoveIcon /> : <AddIcon />}
          onClick={() => setShowFilters((s) => !s)}
          style={{
            textTransform: "none",
            fontWeight: 500,
            borderRadius: 14,
            borderColor: "#d1dbe7",
            boxShadow: "0 0.6px 3.5px rgba(60,90,160,0.13)",
            color: "#345fff",
          }}
        >
          Search by Filter
        </MuiButton>
      </Box>

      {showFilters && (
        <Formik
          initialValues={{ [MEETING_LABEL]: "DISCUSSION_MEETING" }}
          onSubmit={onSearch}
          innerRef={formRef}
        >
          {({ values, errors, handleChange, setFieldValue, handleSubmit }) => (
            <form style={styles.filters} onSubmit={handleSubmit}>
              <Box className={Style.upperFilters}>
                <Dropdown
                  name={MEETING_LABEL}
                  value={values[MEETING_LABEL]}
                  options={MeetingTypes}
                  label="Meeting Selected"
                  onChange={handleChange}
                  containerStyle={getContainerStyle(isMobile)}
                />
                <Dropdown
                  name={DIVISION_ID}
                  value={values[DIVISION_ID]}
                  options={DIVISION_OPTIONS}
                  label="Division"
                  onChange={handleChange}
                  containerStyle={getContainerStyle(isMobile)}
                  showCross
                  handleClearValue={handleClearType}
                  inputProps={{ required: false }}
                />
              </Box>
              <Box
                className={Style.bottomFilters}
                style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}
              >
                <StructureFilter
                  values={values}
                  errors={errors}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  disabledOSKeys={disabledOSKeys}
                  required={false}
                />
                <Button label="Show data" type="submit" />
              </Box>
            </form>
          )}
        </Formik>
      )}

      <Box
        style={{
          ...styles.chartContainer,
          borderRadius: 20,
          overflow: "hidden",
          marginTop: 12,
          background: "transparent",
          transition: "box-shadow 0.22s",
          boxShadow: hover ? "0 5px 32px 0 rgba(80, 116, 250,.10)" : "none",
        }}
      >
        {!loading ? (
          <HighchartsReact highcharts={Highcharts} options={lineChartOptions} />
        ) : (
          <Box
            style={{
              minHeight: 200,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            Loading...
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default Attendance;
